import NextSelection from '~/constants/new-cars/NextSelection';
import SectionName from '~/constants/new-cars/SectionName';
import type { TruckBodyStyleResult, YearResult } from '~/contracts/new-cars';
import isDoorsResult from '~/utilities/new-cars/isDoorsResult';

type ModelVersionResult = Pick<YearResult, 'next'>;

export default function deriveSectionSequence(
  modelVersionResults: ModelVersionResult[],
  truckBodyStyleResults: TruckBodyStyleResult[] = [],
  hasMultipleYears = false,
  nextSelection?: NextSelection | ''
): SectionName[] {
  const sections: SectionName[] = [];

  if (hasMultipleYears) {
    sections.push(SectionName.Year);
  }

  const hasDataLoaded = modelVersionResults.length > 0;
  // `nextSelection` authoritatively labels the loaded response's level; fall
  // back to result-shape inspection for responses that predate the field.
  const hasModelVersions =
    nextSelection !== undefined
      ? nextSelection === NextSelection.ModelVersion
      : hasDataLoaded &&
        modelVersionResults.some(r => r.next?.modelVersion !== '');
  const hasDoors =
    nextSelection !== undefined
      ? nextSelection === NextSelection.DoorCount
      : hasDataLoaded &&
        modelVersionResults.some(r => isDoorsResult(r as YearResult));
  const hasMultipleResults = modelVersionResults.length > 1;

  // When year is first and not yet selected, modelVersions hasn't loaded — don't
  // show ModelVersion as a placeholder since the actual step isn't known yet.
  if (
    (!hasMultipleYears && !hasDataLoaded) ||
    (hasModelVersions && !hasDoors && hasMultipleResults)
  ) {
    sections.push(SectionName.ModelVersion);
  }

  if (hasDoors && hasMultipleResults) {
    sections.push(SectionName.Doors);
  }

  sections.push(SectionName.Trim);

  if (truckBodyStyleResults.length > 0) {
    sections.push(SectionName.TruckBodyStyle);
  }

  sections.push(SectionName.Options);

  return sections;
}
